import { DragDropModule } from "@angular/cdk/drag-drop";
import {
	HTTP_INTERCEPTORS,
	provideHttpClient,
	withInterceptorsFromDi,
} from "@angular/common/http";
import {
	ErrorHandler,
	Injectable,
	NgModule,
	inject,
	provideAppInitializer,
} from "@angular/core";
import { MatBottomSheetModule } from "@angular/material/bottom-sheet";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MatNativeDateModule,
} from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from "@angular/material/tooltip";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { JWT_OPTIONS, JwtInterceptor, JwtModule } from "@auth0/angular-jwt";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsFormPluginModule } from "@ngxs/form-plugin";
import { NgxsRouterPluginModule, RouterStateSerializer } from "@ngxs/router-plugin";
import { NgxsStoragePluginModule } from "@ngxs/storage-plugin";
import { NgxsModule, Store } from "@ngxs/store";
import * as Sentry from "@sentry/browser";
import { EditorModule, TINYMCE_SCRIPT_SRC } from "@tinymce/tinymce-angular";
import { NgxPermissionsModule } from "ngx-permissions";
import { TourMatMenuModule } from "ngx-ui-tour-md-menu";
import { environment } from "../environments/environment";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app.module.routing";
import { CompanyRegisterFormState } from "./auth/state/company-register-form.state";
import { IncidentReportingFormState } from "./incident-reporting/state/incident-reporting-form.state";
import { ngxsConfig } from "./ngxs.config";
import { OauthValidateDomainFormState } from "./oauth/state/oauth-validate-domain-form.state";
import { InstallComponent } from "./pages/install/install.component";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { PwaInstallPromptComponent } from "./pages/pwa-install-prompt/pwa-install-prompt.component";
import { PwaService } from "./pages/pwa-install-prompt/pwa.service";
import { RateLimitReachedComponent } from "./pages/rate-limit-reached/rate-limit-reached.component";
import { TestLandingPageComponent } from "./pages/test-landing-page/test-landing-page.component";
import { PortalAuthState } from "./portal/shared/state/portal-auth.state";
import { SignInFormState } from "./portal/sign-in/state/sign-in-form.state";
import { ReferrerState } from "./referrer/state/referrer.state";
import { UpdateService } from "./shared/service/app-update.service";
import { HttpErrorInterceptor } from "./shared/service/http-error.interceptor";
import { HttpTimeoutInterceptor } from "./shared/service/http-timeout.interceptor";

// Initialize UpdateService to start service worker update checks
function initializeUpdateService(): void {
	// Instantiate UpdateService to trigger constructor and start SW update checks
	inject(UpdateService);
}
import {
	APP_DATE_FORMATS,
	LuxonDateAdapter,
} from "./shared/service/luxon-date-formatter";
import { RefreshTokenInterceptor } from "./shared/service/refresh-token.interceptor";
import { CustomRouterStateSerializer } from "./shared/service/router-serializer";
import { StoredAuthState } from "./shared/state/stored-auth.state";
import { SelectivePreloadingStrategy } from "./shared/utils/preload-strategies";
import { SignInKioskState } from "./sign-in-kiosk/state/sign-in-kiosk.state";
import { RequestDemoFormState } from "./website/state/request-demo-form.state";
Sentry.init({
	dsn: environment.production
		? "https://4919fb7dd8714c8cb259cc6bcbd91c76@o9463.ingest.sentry.io/70317"
		: "",
	// This enables automatic instrumentation (highly recommended), but is not
	// necessary for purely manual usage
	// integrations: [
	// 	new TracingIntegrations.BrowserTracing({
	// 		tracingOrigins: [
	// 			"localhost",
	// 			"inductforwork.com.au",
	// 			"external-ap-southeast-2.api.inductforwork.com",
	// 			"inductforwork.co.uk",
	// 			"external-eu-west-2.api.inductforwork.com",
	// 		],
	// 	}),
	// ],
	// To set a uniform sample rate
	// tracesSampleRate: 0.2,
	beforeSend(event, hint) {
		const isNonErrorException =
			(event.exception &&
				event.exception.values &&
				event.exception.values[0] &&
				event.exception.values[0].value &&
				event.exception.values[0].value.startsWith(
					"Non-Error exception captured",
				)) ||
			(hint &&
				hint.originalException &&
				hint.originalException["message"] &&
				hint.originalException["message"].startsWith(
					"Non-Error exception captured",
				));
		const NS_ERROR_NOT_INITIALIZED =
			(event.exception &&
				event.exception.values &&
				event.exception.values[0] &&
				event.exception.values[0].value &&
				event.exception.values[0].value.startsWith("NS_ERROR_NOT_INITIALIZED")) ||
			(hint &&
				hint.originalException &&
				hint.originalException["message"] &&
				hint.originalException["message"].startsWith("NS_ERROR_NOT_INITIALIZED"));

		if (isNonErrorException || NS_ERROR_NOT_INITIALIZED) {
			// We want to ignore those kind of errors
			return null;
		}
		return event;
	},
	ignoreErrors: [/ Not Valid/i, /Unauthorized/i, /Invalid /i],
	replaysOnErrorSampleRate: 1.0,

	integrations: [
		Sentry.replayIntegration({
			// Additional SDK configuration goes in here, for example:
			maskAllText: false,
			blockAllMedia: false,
		}),
	],
});

@Injectable({
	providedIn: "root",
})
export class SentryErrorHandler implements ErrorHandler {
	private store = inject(Store);

	handleError(error: any): void {
		// Get user information for Sentry context
		const user = this.store.selectSnapshot(StoredAuthState.user);
		let userName: string = "";

		if (user) {
			userName = this.store.selectSnapshot(StoredAuthState.userLoginDetails);
		}

		// Capture exception with appropriate user context
		// Ensure we pass an actual Error object to Sentry
		let errorToReport: Error;
		if (error.originalError instanceof Error) {
			errorToReport = error.originalError;
		} else if (error instanceof Error) {
			errorToReport = error;
		} else if (error.error instanceof Error) {
			errorToReport = error.error;
		} else {
			// Create a proper Error object from the message
			const message = error.error?.message || error.message || "Unknown error";
			errorToReport = new Error(message);
		}

		Sentry.captureException(errorToReport, {
			captureContext: {
				user: { username: userName },
				extra: {
					timestamp: new Date().toISOString(),
					appVersion: environment.version || "unknown",
					originalErrorData: error,
				},
			},
		});
	}
}

export function jwtOptionsFactory() {
	// Use inject to get dependencies
	const store = inject(Store);

	return {
		tokenGetter: () => {
			const authIdToken = store.selectSnapshot(StoredAuthState.authIdToken);
			return authIdToken;
		},
		allowedDomains: environment.allowedDomains,
		disallowedRoutes: environment.disallowedRoutes,
	};
}
function initializePwa() {
	const pwaService = inject(PwaService);
	return pwaService.initPwaPrompt();
}

@NgModule({
	declarations: [
		AppComponent,
		NotFoundComponent,
		PwaInstallPromptComponent,
		TestLandingPageComponent,
		RateLimitReachedComponent,
		InstallComponent,
	],
	bootstrap: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		MatBottomSheetModule,
		MatToolbarModule,
		MatIconModule,
		MatCardModule,
		MatTooltipModule,
		MatDatepickerModule,
		MatButtonModule,
		RouterModule,
		JwtModule.forRoot({
			jwtOptionsProvider: {
				provide: JWT_OPTIONS,
				useFactory: jwtOptionsFactory,
			},
		}),
		NgxsModule.forRoot(
			[StoredAuthState, PortalAuthState, SignInKioskState, ReferrerState],
			ngxsConfig,
		),
		NgxsStoragePluginModule.forRoot({
			keys: [
				StoredAuthState,
				OauthValidateDomainFormState,
				PortalAuthState,
				RequestDemoFormState,
				CompanyRegisterFormState,
				SignInFormState,
				IncidentReportingFormState,
				SignInKioskState,
				ReferrerState,
			],
		}),
		NgxsReduxDevtoolsPluginModule.forRoot(),
		// NgxsLoggerPluginModule.forRoot(),
		NgxsRouterPluginModule.forRoot(),
		NgxsFormPluginModule.forRoot(),
		BrowserAnimationsModule,
		NgxPermissionsModule.forRoot(),
		MatSnackBarModule,
		DragDropModule,
		MatNativeDateModule,
		MatDialogModule,
		ServiceWorkerModule.register("ngsw-worker.js", {
			enabled: environment.production,
		}),
		TourMatMenuModule,
		EditorModule,
	],
	providers: [
		UpdateService,
		JwtInterceptor,
		{
			provide: ErrorHandler,
			useClass: environment.production ? SentryErrorHandler : ErrorHandler,
		},
		{ provide: DateAdapter, useClass: LuxonDateAdapter },
		{ provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
		{ provide: HTTP_INTERCEPTORS, useClass: RefreshTokenInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: HttpTimeoutInterceptor, multi: true },
		provideAppInitializer(initializePwa),
		provideAppInitializer(initializeUpdateService),
		{ provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
		{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { position: "above" } },
		{ provide: TINYMCE_SCRIPT_SRC, useValue: "tinymce/tinymce.min.js" },
		provideHttpClient(withInterceptorsFromDi()),
		SelectivePreloadingStrategy,
	],
})
export class AppModule {}
